/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import InvoiceBulkCreateService, {
  BulkInvoiceCreateFilter,
} from '@/services/InvoiceBulkCreateService';

// There's no cap on how many rows a user can select -- a selection larger than one chunk is
// split into multiple sequential bulk requests instead. Chunk size is chosen independently of
// InvoicesController.MaxBulkFilterCount (the backend's per-request ceiling, kept only as
// defense-in-depth) -- keep this well under that so a single chunk is never rejected by it.
// Sized so a realistic chunk finishes with roughly 2x headroom under BULK_CREATE_TIMEOUT_MS
// even for a slow firm (~11s/invoice measured).
const CHUNK_SIZE = 15;

// axios only aborts a request when `timeout` is set -- without it a stalled connection hangs
// indefinitely, leaving `creating` stuck forever. Kept a bit under InvoiceCreator's Redis lock
// TTL (next-backend): if the client gives up right around when the server-side lock would
// anyway expire, a resubmit isn't left guessing for much longer.
const BULK_CREATE_TIMEOUT_MS = 6 * 60 * 1000;

// Survives a page reload (sessionStorage), cleared once every chunk of the current attempt has
// a definitive result (see the `finally` below). InvoiceCreator.CreateBulkFromFiltersAsync
// (next-backend) takes each chunk's batchKey as a Guid and uses it as a Redis lock key that's
// held only for that request's duration -- it does NOT remember past results, so reusing a key
// only protects against a *concurrent* duplicate call with the same key (double-click, or a
// resubmit while the original request from before a refresh is still in flight server-side). If
// the original already finished, the lock is already released and a same-key resubmit is not
// deduped either -- so this only matters for the "still possibly in flight" window.
const PENDING_BULK_CREATE_BATCH_KEYS = 'invoiceStaging.pendingBulkCreateBatchKeys';

export interface RunBulkCreateChunkResult {
  rowKey: string;
  error: string | null;
}

export interface RunBulkCreateSummary {
  total: number;
  failedCount: number;
  successMessagePlural: string;
}

export interface RunBulkCreateOptions {
  filters: BulkInvoiceCreateFilter[];
  // One key per filter, same order -- `client:<id>` or `matter:<id>`, matching how the caller
  // re-identifies a row after a fresh fetch. The store never touches AngularJS scope objects
  // directly: it has to survive the controller that started it being destroyed by an in-app
  // navigation, so it can only deal in plain, serializable data.
  rowKeys: string[];
  method: string;
  draft: boolean;
  successMessagePlural: string;
  // Fired after each chunk's response/catch, while the caller (if any) is still mounted --
  // lets it paint results onto the currently-rendered table immediately rather than waiting
  // for the whole run (and a fresh fetch) to reflect them.
  onChunkComplete?: (results: RunBulkCreateChunkResult[]) => void;
}

function chunkArray<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

// axios's rejection shape differs from the legacy $http/leanLawApi one (`err.body.message`) --
// this reads `err.response`, not `err.body`.
function bulkCreateErrorMessage(err: unknown): { message: string; ambiguous: boolean } {
  const response = (err as { response?: { status?: number; data?: { message?: string } } })
    ?.response;
  const responseReceived = typeof response?.status === 'number' && response.status > 0;

  if (response?.status === 409) {
    return {
      message: 'This batch is already being processed. Please wait for it to finish before trying again.',
      ambiguous: false,
    };
  }

  if (!responseReceived) {
    // Must not assert a duplicate risk -- the server-side Redis idempotency guard
    // (InvoiceCreator.CreateBulkFromFiltersAsync) makes a retry safe within its replay window,
    // so a retrying user is never actually risking a duplicate invoice.
    return {
      message:
        'This request timed out. It may have still completed -- wait a few minutes and check Ready to Bill before retrying. Retrying is safe and will not create duplicate invoices.',
      ambiguous: true,
    };
  }

  return { message: response?.data?.message || 'Failed to create this invoice.', ambiguous: false };
}

/**
 * Owns the RTB "Create Draft Invoices" chunk-submit loop as a singleton, outside any AngularJS
 * controller's `$scope`. A run (and this store's tracking of it) keeps going regardless of what
 * page is open, since nothing here depends on any one controller instance staying alive.
 *
 * Not a @pinia/colada store: this isn't a cacheable read or a single request/response
 * mutation, it's a long-running, multi-request background job with its own progress and
 * partial-failure bookkeeping across chunks. Colada's query/mutation shapes don't fit that.
 */
export const useBulkInvoiceCreateStore = defineStore('bulkInvoiceCreate', () => {
  const creating = ref(false);
  const creatingTotal = ref(0);
  const creatingProcessed = ref(0);
  const creatingChunk = ref(0);
  const creatingChunkTotal = ref(0);
  const draft = ref(true);
  const lastRunSummary = ref<RunBulkCreateSummary | null>(null);

  // Epoch ms when the current run started, or null when idle. Progress otherwise only advances
  // at chunk boundaries (there's no per-invoice signal from a single request/response endpoint),
  // which can be minutes apart -- an elapsed-time readout is what tells a long run apart from a
  // stuck one in between those updates.
  const runStartedAt = ref<number | null>(null);

  // rowKey -> error message, or null for a success. Keyed by the same `client:<id>` /
  // `matter:<id>` scheme the caller uses, so results outlive both the run and whichever
  // controller instance kicked it off -- a fresh RTB page load can still ask for them.
  const resultsByRowKey = ref(new Map<string, string | null>());

  interface ClientRow {
    client: { id: string };
    error?: string | null;
    matters: Array<{ matter: { id: string }; error?: string | null }>;
  }

  // Mirrors invoice.staging.ctrl.js's data shape: a client row can itself be a submitted row
  // (`client:<id>`), and/or hold matter rows that were submitted individually (`matter:<id>`) --
  // a batch can mix both in the same run, so both are checked for every client.
  function applyResultsToRows(clients: ClientRow[]): void {
    if (resultsByRowKey.value.size === 0) return;
    clients.forEach((client) => {
      const clientError = resultsByRowKey.value.get(`client:${client.client.id}`);
      if (clientError) client.error = clientError;
      client.matters.forEach((matter) => {
        const matterError = resultsByRowKey.value.get(`matter:${matter.matter.id}`);
        if (matterError) matter.error = matterError;
      });
    });
    // Once the run that produced these results has finished, this fetch is the delivery a
    // controller that wasn't mounted for completion was waiting for -- clear them so a row
    // whose key merely happens to recur in some later, unrelated fetch this session doesn't
    // keep getting an error that belongs to a run it was never part of. While a run is still
    // in progress (chunks yet to land), leave them for the next reapplication.
    if (!creating.value) {
      resultsByRowKey.value.clear();
    }
  }

  function clearResults(): void {
    resultsByRowKey.value.clear();
    lastRunSummary.value = null;
  }

  async function runBulkCreate(options: RunBulkCreateOptions): Promise<void> {
    if (creating.value) return;

    const { filters, rowKeys, method, successMessagePlural, onChunkComplete } = options;
    draft.value = options.draft;
    clearResults();

    creating.value = true;
    creatingTotal.value = filters.length;
    creatingProcessed.value = 0;
    runStartedAt.value = Date.now();

    const filterChunks = chunkArray(filters, CHUNK_SIZE);
    const rowKeyChunks = chunkArray(rowKeys, CHUNK_SIZE);
    creatingChunkTotal.value = filterChunks.length;
    let failedCount = 0;

    // Set when a chunk's outcome is unknown (timeout, dropped connection -- no response
    // reached us at all) rather than definite. Guards whether it's safe to hand out fresh
    // batchKeys on the next attempt -- see the `finally` block below.
    let hasAmbiguousOutcome = false;

    let chunkKeys: string[] | null = null;

    // Reading/writing the reserved batchKeys and running randomUUID() live inside this try too:
    // if sessionStorage throws (private-browsing quota, storage disabled) the state below never
    // gets reset on its own, leaving `creating` (and the elapsed-time clock reading it) stuck on
    // forever -- see the `finally` block.
    try {
      try {
        const stored = JSON.parse(sessionStorage.getItem(PENDING_BULK_CREATE_BATCH_KEYS) || 'null');
        chunkKeys = Array.isArray(stored) && stored.length === filterChunks.length ? stored : null;
      } catch {
        chunkKeys = null;
      }
      if (!chunkKeys) {
        chunkKeys = filterChunks.map(() => crypto.randomUUID());
      }
      sessionStorage.setItem(PENDING_BULK_CREATE_BATCH_KEYS, JSON.stringify(chunkKeys));

      for (let i = 0; i < filterChunks.length; i += 1) {
        const chunkFilters = filterChunks[i];
        const chunkRowKeys = rowKeyChunks[i];
        creatingChunk.value = i + 1;

        const chunkResults: RunBulkCreateChunkResult[] = [];
        try {
          const response = await InvoiceBulkCreateService.createBulk(
            chunkFilters,
            chunkKeys[i],
            method,
            BULK_CREATE_TIMEOUT_MS,
          );
          const results = response?.results || [];
          chunkRowKeys.forEach((rowKey, index) => {
            const result = results[index];
            const error = !result || !result.success ? result?.error || 'Failed to create this invoice.' : null;
            if (error) failedCount += 1;
            resultsByRowKey.value.set(rowKey, error);
            chunkResults.push({ rowKey, error });
          });
        } catch (err) {
          const { message, ambiguous } = bulkCreateErrorMessage(err);
          if (ambiguous) hasAmbiguousOutcome = true;
          chunkRowKeys.forEach((rowKey) => {
            resultsByRowKey.value.set(rowKey, message);
            chunkResults.push({ rowKey, error: message });
          });
          failedCount += chunkRowKeys.length;
        }

        onChunkComplete?.(chunkResults);
        creatingProcessed.value += chunkFilters.length;
      }

      lastRunSummary.value = { total: filters.length, failedCount, successMessagePlural };
    } finally {
      // Only safe to free the reserved keys once every chunk reached a definite outcome --
      // see hasAmbiguousOutcome above.
      if (!hasAmbiguousOutcome) {
        sessionStorage.removeItem(PENDING_BULK_CREATE_BATCH_KEYS);
      }
      creating.value = false;
      creatingTotal.value = 0;
      creatingProcessed.value = 0;
      creatingChunk.value = 0;
      creatingChunkTotal.value = 0;
      runStartedAt.value = null;
    }
  }

  return {
    creating,
    creatingTotal,
    creatingProcessed,
    creatingChunk,
    creatingChunkTotal,
    draft,
    lastRunSummary,
    resultsByRowKey,
    runStartedAt,
    runBulkCreate,
    applyResultsToRows,
    clearResults,
  };
});
